// DAS-2132: "Your Asset Allocation" modal content for PortfolioAnalysisView
// Computes everything client-side from the data prop — no API call needed.
// Shows donut chart of 6 asset classes, strategy comparison table + bar chart,
// closest strategy match, wealth assumptions, and optional non-broker asset enrichment.

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Chip, LinearProgress, Alert, AlertTitle,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Collapse, TextField, InputAdornment,
  Button, Tooltip, Dialog, DialogTitle, DialogContent, IconButton,
  TableSortLabel,
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ─── Constants ───────────────────────────────────────────────────

const ASSET_CLASS_COLORS = {
  'Equity':        '#1976d2',
  'Fixed Income':  '#7b1fa2',
  'Cash':          '#2e7d32',
  'Real Assets':   '#e65100',
  'Alternatives':  '#455a64',
  'Crypto':        '#f57c00',
  'Unclassified':  '#9e9e9e',
};

const INVESTABLE_CLASSES = ['Equity', 'Fixed Income', 'Cash', 'Real Assets', 'Alternatives', 'Crypto'];
const ASSET_CLASS_ORDER = [...INVESTABLE_CLASSES, 'Unclassified'];

const ASSET_CLASS_INFO = {
  'Equity': {
    subtitle: 'Stocks',
    description: 'Ownership stakes in companies.',
    subdivisions: [
      'Market cap: Large-cap, mid-cap, small-cap, micro-cap',
      'Geography: Domestic, international developed, emerging markets, frontier',
      'Style: Growth, value, blend',
      'Sector: Tech, healthcare, financials, energy, consumer, industrials, utilities, real estate, materials, communications',
    ],
  },
  'Fixed Income': {
    subtitle: 'Bonds',
    description: 'Debt instruments paying interest.',
    subdivisions: [
      'Issuer: Government (Treasuries, munis), corporate, agency',
      'Duration: Short (<3yr), intermediate (3–10yr), long (10yr+)',
      'Credit quality: Investment grade (AAA–BBB), high yield/junk (BB and below)',
      'Type: Nominal, inflation-linked (TIPS), floating rate, convertible',
    ],
  },
  'Cash': {
    subtitle: 'Cash & Equivalents',
    description: 'Deposits, savings, money markets, T-bills, CDs. Capital guaranteed, near-zero risk.',
    subdivisions: [],
  },
  'Real Assets': {
    subtitle: 'Tangible / inflation-linked',
    description: 'Physical assets and commodities.',
    subdivisions: [
      'Real estate: Direct property, REITs, mortgage-backed securities',
      'Commodities: Energy (oil, gas), metals (gold, silver, copper), agriculture',
      'Infrastructure: Toll roads, utilities, airports (often inflation-linked)',
      'Timberland / farmland',
    ],
  },
  'Alternatives': {
    subtitle: 'Non-traditional',
    description: 'Investments outside public equity and fixed income.',
    subdivisions: [
      'Private equity: Buyout, venture capital, growth equity',
      'Hedge funds: Long/short, macro, arbitrage, quant',
      'Private credit/debt: Direct lending, distressed debt, mezzanine',
      'Collectibles & art: Illiquid, speculative',
    ],
  },
  'Crypto': {
    subtitle: 'Digital Assets',
    description: 'High volatility, speculative, non-correlated (increasingly debated).',
    subdivisions: [],
  },
  'Unclassified': {
    subtitle: 'Needs review',
    description: 'Instruments where the asset class could not be determined from available data. ETFs are wrappers, not an asset class — they are classified by underlying exposure when possible.',
    subdivisions: [
      'No explicit asset type was provided in the source data',
      'Name-based heuristics did not match a known pattern',
      'Review these holdings and reclassify if needed',
    ],
  },
};

const STRATEGIES = {
  'Capital Preservation': {
    Equity: [10, 20], 'Fixed Income': [60, 70], Cash: [10, 20],
    'Real Assets': [0, 5], Alternatives: [0, 5], Crypto: [0, 0],
    profile: 'Retirees, short horizon',
    explanation: 'Prioritises safety over growth. Mostly bonds and cash, very little in stocks. For people who cannot afford losses — e.g. retirees living off their savings.',
  },
  'Conservative': {
    Equity: [20, 40], 'Fixed Income': [50, 60], Cash: [5, 10],
    'Real Assets': [0, 5], Alternatives: [0, 10], Crypto: [0, 0],
    profile: 'Low risk tolerance',
    explanation: 'Still cautious — more bonds than stocks. Accepts small ups and downs for slightly higher returns than pure cash. Suitable if you need the money in the next 5–10 years.',
  },
  'Balanced / Moderate': {
    Equity: [50, 60], 'Fixed Income': [30, 40], Cash: [3, 5],
    'Real Assets': [0, 5], Alternatives: [5, 10], Crypto: [0, 2],
    profile: 'Medium horizon, moderate risk',
    explanation: 'Roughly half stocks, half bonds. The classic "middle road" — expects decent long-term returns but will drop noticeably in bad years. Good for 10+ year horizons.',
  },
  'Growth': {
    Equity: [70, 80], 'Fixed Income': [15, 25], Cash: [2, 5],
    'Real Assets': [0, 5], Alternatives: [5, 15], Crypto: [0, 5],
    profile: 'Long horizon, higher risk',
    explanation: 'Mostly stocks, some bonds for stability. Aims for strong long-term returns but can drop 30–40% in bad years. You need a 15+ year horizon and the stomach to sit through crashes.',
  },
  'Aggressive Growth': {
    Equity: [85, 100], 'Fixed Income': [0, 10], Cash: [0, 2],
    'Real Assets': [0, 5], Alternatives: [10, 20], Crypto: [0, 10],
    profile: 'Long horizon, high risk tolerance',
    explanation: 'Nearly all stocks and high-risk assets. Maximum long-term growth, but can lose 40–50% in a crisis. Only for people who won\'t need this money for 20+ years and can handle extreme volatility.',
  },
  'All-Weather': {
    Equity: [25, 35], 'Fixed Income': [50, 60], Cash: [0, 2],
    'Real Assets': [10, 20], Alternatives: [5, 15], Crypto: [0, 2],
    profile: 'Balanced across economic regimes',
    explanation: 'Inspired by Ray Dalio — spreads money across asset classes that do well in different economic climates (growth, recession, inflation, deflation). Lower highs but also lower lows. Requires significant real assets (property, gold, commodities).',
  },
};

const NON_BROKER_CATEGORIES = [
  { key: 'real_estate', label: 'Own home / real estate', icon: '🏠' },
  { key: 'savings',     label: 'Cash & savings accounts', icon: '💰' },
  { key: 'pension',     label: 'Pension / retirement fund', icon: '🏦' },
  { key: 'other_broker', label: 'Other broker accounts', icon: '🌐' },
  { key: 'other',       label: 'Other (business, collectibles…)', icon: '📦' },
];

const NON_BROKER_TO_ASSET_CLASS = {
  real_estate:  'Real Assets',
  savings:      'Cash',
  pension:      'Fixed Income',
  other_broker: 'Equity',
  other:        'Alternatives',
};

// ─── Classification heuristics (JS port of DR_AssetAllocation.py) ─

const FI_RE = /bond|treasury|treasuries|fixed.?income|aggregate.?bond|govt|government|tips|inflation.?protect|investment.?grade|high.?yield|corporate.?bond|municipal|muni|credit|duration|coupon|debt|gilts|bund|obligatie|staatsobligatie|bedrijfsobligatie|\babs\b|asset.?backed/i;
const REAL_RE = /real.?estate|reit|property|properties|commodity|commodities|gold|silver|platinum|palladium|oil|natural.?gas|energy.?commodity|timber|farmland|infrastructure|agriculture|metals|mining|copper|uranium|vastgoed|onroerend|woning|appartement|huurwoning|pand/i;
const CRYPTO_RE = /crypto|bitcoin|btc|ethereum|eth|blockchain|digital.?asset|defi|web3/i;
const ALT_RE = /hedge|long.?short|market.?neutral|merger.?arbitrage|managed.?futures|private.?equity|private.?credit|venture|mezzanine|distressed|multi.?strategy|absolute.?return|volatility|vix/i;

const DIRECT_MAP = {
  Stock: 'Equity', STOCK: 'Equity', Equity: 'Equity', EQUITY: 'Equity',
  'Depositary Receipt': 'Equity', 'DEPOSITARY RECEIPT': 'Equity',
  ADR: 'Equity', GDR: 'Equity',
  Bond: 'Fixed Income', BOND: 'Fixed Income', 'Fixed Income': 'Fixed Income',
  Cash: 'Cash', CASH: 'Cash', Savings: 'Cash', Deposits: 'Cash',
  'Money Market': 'Cash',
  Cryptocurrency: 'Crypto', CRYPTOCURRENCY: 'Crypto', Crypto: 'Crypto',
  Commodities: 'Real Assets', 'Real Assets': 'Real Assets',
  'Corporate Bonds': 'Fixed Income',
  Alternatives: 'Alternatives',
};

const CASH_RE = /\bcash\b|money.?market|savings|deposit|liquidity|liquiditeiten|sweep/i;
const FI_NAME_OVERRIDE_RE = /\b\d+(\.\d+)?%\s*(frn|perp|bond|note|debenture|coupon|float|fixed)\b|\b(frn|perpetual|perp)\b.*\b\d+(\.\d+)?%\b/i;
// DAS-2764: for FUNDS, "Real Assets" requires a genuinely physical/commodity or
// real-estate product (Physical Gold ETC, REIT fund, spot commodity). A fund named
// after a commodity THEME (uranium, mining, infrastructure, agriculture, ...) holds
// company SHARES and must classify as Equity — the broad REAL_RE stays in use only
// for non-fund rows (manual real estate: woning/vastgoed/pand etc.).
const REAL_FUND_RE = /real.?estate|reit|\bphysical\b|\betc\b|commodit|spot.?(gold|silver)|precious.?metals?|vastgoed|onroerend/i;

function classifyHolding(assetType, companyName, sector) {
  const sectorLower = (sector || '').toLowerCase();
  if (sectorLower.includes('reit') || sectorLower.includes('real estate')) return 'Real Assets';

  const name = companyName || '';

  if (FI_NAME_OVERRIDE_RE.test(name)) return 'Fixed Income';

  const at = (assetType || '').trim();
  if (DIRECT_MAP[at]) return DIRECT_MAP[at];

  // ETFs are wrappers — classify by underlying exposure via name heuristics
  const atUp = at.toUpperCase();
  if (['ETF', 'MUTUALFUND', 'ETN', 'EQUITY FUND'].includes(atUp)) {
    if (FI_RE.test(name)) return 'Fixed Income';
    if (REAL_FUND_RE.test(name)) return 'Real Assets';  // DAS-2764: physical/RE only
    if (CRYPTO_RE.test(name)) return 'Crypto';
    if (ALT_RE.test(name)) return 'Alternatives';
    if (CASH_RE.test(name)) return 'Cash';
    if (/equity|stock|s&p|nasdaq|dow|russell|msci|ftse|stoxx|cac|dax|nikkei|topix|hang.?seng|index|world|global|developed|emerging|small.?cap|mid.?cap|large.?cap|growth|value|dividend|quality|momentum|sector|tech|health|financ|consumer|industr|energy|utilit|europa|noord.?amerika|azie|opkomende.?landen|nederland/i.test(name)) return 'Equity';
    return 'Equity';
  }

  // Name-based fallback for items with no explicit asset type
  if (CASH_RE.test(name)) return 'Cash';
  if (FI_RE.test(name)) return 'Fixed Income';
  if (REAL_RE.test(name)) return 'Real Assets';
  if (CRYPTO_RE.test(name)) return 'Crypto';
  if (ALT_RE.test(name)) return 'Alternatives';

  // No positive evidence — don't silently assume Equity
  return 'Unclassified';
}

// ─── Allocation engine ───────────────────────────────────────────

function getHoldingValue(d) {
  const v = parseFloat(d.Value_eur);
  if (v > 0) return v;

  const qty = parseFloat(d.Position) || parseFloat(d.position_size) || 0;
  const price = parseFloat(d.purchase_cost) || parseFloat(d.latest_price) || 0;
  if (qty > 0 && price > 0) return qty * price;

  const exp = parseFloat(d.exposure_pct);
  if (exp > 0) return exp;

  return 0;
}

function computeAllocation(data, extraAssets = {}, etfStockPositions = [], etfLeftoverPositions = []) {
  const buckets = Object.fromEntries(ASSET_CLASS_ORDER.map(ac => [ac, 0]));
  const holdingsByClass = Object.fromEntries(ASSET_CLASS_ORDER.map(ac => [ac, []]));

  // Build set of ETF tickers that have been dissected (have look-through data)
  const dissectedEtfTickers = new Set();
  if (etfStockPositions.length > 0) {
    for (const d of data) {
      if (d.top_holdings && d.top_holdings.length > 0) {
        const ticker = (d.Ticker || d.ticker || d.identifier || '').toUpperCase();
        if (ticker) dissectedEtfTickers.add(ticker);
      }
    }
  }

  // Classify ALL records, even those without values (for class-only reporting)
  const classOnlyByClass = Object.fromEntries(ASSET_CLASS_ORDER.map(ac => [ac, []]));

  for (const d of data) {
    const at = d.asset_type || d.AssetType || d['Asset Class'] || d.Quotetype || d.asset_class || '';
    const company = d.Company || d.company || d.NAME || d.name || '';
    const ticker = d.Ticker || d.ticker || d.identifier || '';
    const sector = d.sector || d.geography || '';
    const currency = d.currency || '';

    if (dissectedEtfTickers.has(ticker.toUpperCase())) continue;

    const ac = classifyHolding(at, company, sector);
    const val = getHoldingValue(d);

    classOnlyByClass[ac].push({ name: company || ticker || '(unknown)', ticker, type: at || null, classifiedAs: ac });

    if (val <= 0) continue;
    buckets[ac] += val;
    holdingsByClass[ac].push({ name: company || ticker || '(unknown)', ticker, value: val, type: at || null, classifiedAs: ac, sector, currency, source: 'direct' });
  }

  // Build ETF value lookup for computing holding percentages within each ETF
  const etfValueMap = {};
  for (const d of data) {
    const t = (d.Ticker || d.ticker || d.identifier || '').toUpperCase();
    if (t && dissectedEtfTickers.has(t)) {
      etfValueMap[t] = getHoldingValue(d);
    }
  }

  // Process look-through stock positions from dissected ETFs
  for (const sp of etfStockPositions) {
    const val = sp.total_value || 0;
    if (val <= 0) continue;
    const name = sp.name || sp.symbol || '';
    const ticker = sp.symbol || '';
    const etfDetails = (sp.etfs || []).map(e => {
      const etfTicker = e.ticker || e.name || '?';
      const etfTotal = etfValueMap[etfTicker.toUpperCase()] || 0;
      const pctInEtf = etfTotal > 0 ? Math.round((e.value || 0) / etfTotal * 1000) / 10 : null;
      return { etfTicker, value: e.value || 0, pctInEtf };
    });
    const etfSources = etfDetails.map(e => e.pctInEtf != null ? `${e.etfTicker} (${fmtPct(e.pctInEtf)})` : e.etfTicker).join(', ');
    const ac = classifyHolding('', name, '');
    const finalAc = ac === 'Unclassified' ? 'Equity' : ac;
    buckets[finalAc] += val;
    holdingsByClass[finalAc].push({ name, ticker, value: val, type: 'Stock (via ETF)', classifiedAs: finalAc, sector: '', currency: sp.currency || '', source: 'etf_lookthrough', etfSources, etfDetails });
  }

  // Process leftover (unidentified) portions of dissected ETFs
  for (const lp of etfLeftoverPositions) {
    const val = lp.leftover_value || 0;
    if (val <= 0) continue;
    const etfName = lp.name || lp.ticker || '';
    const etfTicker = lp.ticker || '';
    const ac = classifyHolding('ETF', etfName, '');
    buckets[ac] += val;
    holdingsByClass[ac].push({
      name: `${etfName} (unidentified ${fmtPct(lp.leftover_pct)} of ETF)`,
      ticker: etfTicker, value: val, type: 'ETF leftover', classifiedAs: ac, sector: '', currency: lp.currency || '',
      source: 'etf_leftover', etfSources: etfTicker, etfDetails: [{ etfTicker, pctInEtf: lp.leftover_pct }],
    });
  }

  Object.entries(extraAssets).forEach(([key, val]) => {
    if (val > 0) {
      const ac = NON_BROKER_TO_ASSET_CLASS[key] || 'Alternatives';
      buckets[ac] += val;
      const cat = NON_BROKER_CATEGORIES.find(c => c.key === key);
      holdingsByClass[ac].push({ name: cat?.label || key, ticker: '', value: val, type: 'Manual', sector: '', currency: '', source: 'manual' });
    }
  });

  const total = Object.values(buckets).reduce((s, v) => s + v, 0);
  if (total <= 0) {
    const totalClassified = Object.values(classOnlyByClass).reduce((s, arr) => s + arr.length, 0);
    if (totalClassified === 0) return null;
    return {
      classificationOnly: true,
      class_summary: ASSET_CLASS_ORDER
        .filter(name => classOnlyByClass[name].length > 0)
        .map(name => ({ name, count: classOnlyByClass[name].length, holdings: classOnlyByClass[name] })),
      total_classified: totalClassified,
    };
  }

  for (const ac of ASSET_CLASS_ORDER) {
    holdingsByClass[ac].sort((a, b) => b.value - a.value);
    holdingsByClass[ac].forEach(h => { h.pct = Math.round((h.value / total) * 1000) / 10; });
  }

  const assetClasses = ASSET_CLASS_ORDER.map(name => ({
    name,
    value_eur: buckets[name],
    percentage: Math.round((buckets[name] / total) * 1000) / 10,
    source: 'analyzed',
  }));

  const pctMap = Object.fromEntries(assetClasses.map(ac => [ac.name, ac.percentage]));
  const closestResult = findClosestStrategy(pctMap);
  const comparison = buildStrategyComparison(pctMap);
  const assumptions = buildAssumptions(pctMap, total);

  return { asset_classes: assetClasses, total_value: total, closest_strategy: closestResult.name, strategy_match: closestResult, strategy_comparison: comparison, strategy_assumptions: assumptions, holdings_by_class: holdingsByClass };
}

function findClosestStrategy(pctMap) {
  let bestName = null, bestDist = Infinity;
  const details = {};
  for (const [name, ranges] of Object.entries(STRATEGIES)) {
    let dist = 0;
    let inRange = 0;
    const misses = [];
    for (const ac of INVESTABLE_CLASSES) {
      const [lo, hi] = ranges[ac] || [0, 0];
      const mid = (lo + hi) / 2;
      const actual = pctMap[ac] || 0;
      dist += (actual - mid) ** 2;
      if (actual >= lo && actual <= hi) {
        inRange++;
      } else {
        const diff = actual < lo ? lo - actual : actual - hi;
        misses.push({ ac, actual: Math.round(actual * 10) / 10, lo, hi, diff: Math.round(diff * 10) / 10 });
      }
    }
    dist = Math.sqrt(dist);
    details[name] = { distance: Math.round(dist * 10) / 10, inRange, total: INVESTABLE_CLASSES.length, misses };
    if (dist < bestDist) { bestDist = dist; bestName = name; }
  }
  const best = details[bestName] || {};
  return {
    name: bestName,
    distance: best.distance,
    inRange: best.inRange,
    total: best.total,
    misses: best.misses || [],
    isGoodFit: best.inRange >= 4,
    isReasonableFit: best.inRange >= 2,
    allDetails: details,
  };
}

function buildStrategyComparison(pctMap) {
  const result = {};
  for (const [name, ranges] of Object.entries(STRATEGIES)) {
    const row = { profile: ranges.profile, asset_classes: {} };
    for (const ac of INVESTABLE_CLASSES) {
      const [lo, hi] = ranges[ac] || [0, 0];
      row.asset_classes[ac] = { range_low: lo, range_high: hi, portfolio_actual: Math.round((pctMap[ac] || 0) * 10) / 10 };
    }
    result[name] = row;
  }
  return result;
}

function buildAssumptions(pctMap, totalValue) {
  const eqPct = pctMap.Equity || 0;
  if (eqPct <= 0 || totalValue <= 0) return {};
  const result = {};
  for (const [name, ranges] of Object.entries(STRATEGIES)) {
    const [lo, hi] = ranges.Equity || [0, 0];
    const mid = (lo + hi) / 2;
    if (mid <= 0) continue;
    const impliedTotal = totalValue / (eqPct / 100) * (mid / 100);
    const impliedNonBroker = Math.max(0, impliedTotal - totalValue);
    const brokerShare = totalValue / impliedTotal * 100;
    result[name] = {
      implied_total_wealth: Math.round(impliedTotal * 100) / 100,
      implied_non_broker_assets: Math.round(impliedNonBroker * 100) / 100,
      broker_share_pct: Math.round(brokerShare * 10) / 10,
      profile: ranges.profile,
    };
  }
  return result;
}

// ─── Formatters ──────────────────────────────────────────────────

const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `€${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const fmtPct = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `${Number(v).toFixed(1)}%`;
};

// ─── Tabs ────────────────────────────────────────────────────────

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'insights', label: 'What This Means' },
  { key: 'strategy', label: 'Strategy Comparison' },
  { key: 'assumptions', label: 'Wealth Assumptions' },
];

// ─── Asset class info icon with tooltip ─────────────────────────

const AssetClassInfoIcon = ({ className }) => {
  const info = ASSET_CLASS_INFO[className];
  if (!info) return null;
  return (
    <Tooltip
      placement="top"
      arrow
      slotProps={{ tooltip: { sx: { bgcolor: 'rgba(33,33,33,0.97)', p: 1.5, maxWidth: 340 } } }}
      title={
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.3 }}>
            {className} ({info.subtitle})
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: info.subdivisions.length ? 0.5 : 0 }}>
            {info.description}
          </Typography>
          {info.subdivisions.map((s, i) => (
            <Typography key={i} variant="caption" sx={{ display: 'block', pl: 1, lineHeight: 1.5 }}>
              • {s}
            </Typography>
          ))}
        </Box>
      }
    >
      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help', ml: 0.3 }} />
    </Tooltip>
  );
};

// ─── Hover tooltip content (top N holdings) ─────────────────────

const HoldingsTooltipContent = ({ holdings = [], className }) => {
  if (!holdings.length) return <Typography variant="caption">No holdings</Typography>;
  const top = holdings.slice(0, 6);
  const remaining = holdings.length - top.length;
  return (
    <Box sx={{ minWidth: 180, maxWidth: 280 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}>
        {className} — {holdings.length} holding{holdings.length !== 1 ? 's' : ''}
      </Typography>
      {top.map((h, i) => (
        <Box key={i} display="flex" justifyContent="space-between" gap={1}>
          <Typography variant="caption" noWrap sx={{ maxWidth: 170 }}>
            {h.ticker ? `${h.ticker}` : h.name}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
            {fmtPct(h.pct)}
          </Typography>
        </Box>
      ))}
      {remaining > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
          + {remaining} more — click to view all
        </Typography>
      )}
    </Box>
  );
};

// ─── Detail dialog (full table for one asset class) ─────────────

const AssetClassDetailDialog = ({ open, onClose, className, holdings = [], totalValue, isWeightsOnly = false }) => {
  const [orderBy, setOrderBy] = useState('value');
  const [orderDir, setOrderDir] = useState('desc');

  const handleSort = useCallback((field) => {
    setOrderDir(prev => (orderBy === field && prev === 'desc') ? 'asc' : 'desc');
    setOrderBy(field);
  }, [orderBy]);

  const sorted = useMemo(() => {
    const copy = [...holdings];
    const dir = orderDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      if (orderBy === 'name' || orderBy === 'ticker' || orderBy === 'type' || orderBy === 'source')
        return dir * (a[orderBy] || '').localeCompare(b[orderBy] || '');
      return dir * ((a[orderBy] || 0) - (b[orderBy] || 0));
    });
    return copy;
  }, [holdings, orderBy, orderDir]);

  const classTotal = holdings.reduce((s, h) => s + h.value, 0);
  const color = ASSET_CLASS_COLORS[className] || '#666';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        {className}
        <AssetClassInfoIcon className={className} />
        <Chip label={`${holdings.length} holding${holdings.length !== 1 ? 's' : ''}`} size="small" sx={{ ml: 0.5 }} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', mr: 4 }}>
          {isWeightsOnly
            ? `${totalValue > 0 ? fmtPct(classTotal / totalValue * 100) : '—'} of portfolio`
            : `${fmtCurrency(classTotal)} · ${totalValue > 0 ? fmtPct(classTotal / totalValue * 100) : '—'} of portfolio`}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TableContainer>
          <MuiTable size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                {[
                  { id: 'name', label: 'Name', align: 'left' },
                  { id: 'ticker', label: 'Ticker', align: 'left' },
                  { id: 'type', label: 'Type', align: 'left' },
                  { id: 'source', label: 'Source', align: 'left' },
                  { id: 'value', label: isWeightsOnly ? 'Weight' : 'Value (EUR)', align: 'right' },
                  { id: 'pct', label: '% of Portfolio', align: 'right' },
                ].map(col => (
                  <TableCell key={col.id} align={col.align} sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? orderDir : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((h, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell>{h.name || '—'}</TableCell>
                  <TableCell>{h.ticker ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{h.ticker}</Typography> : '—'}</TableCell>
                  <TableCell>
                    {h.type
                      ? h.type
                      : <Tooltip title="No asset type in source data — classified by heuristics" arrow>
                          <Typography variant="body2" component="span" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.8rem', cursor: 'help' }}>
                            no source type
                          </Typography>
                        </Tooltip>
                    }
                  </TableCell>
                  <TableCell>
                    {(h.source === 'etf_lookthrough' || h.source === 'etf_leftover') ? (
                      <Box>
                        <Chip
                          label={h.source === 'etf_lookthrough' ? 'ETF look-through' : 'ETF remainder'}
                          size="small" variant="outlined"
                          color={h.source === 'etf_leftover' ? 'warning' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem', mb: 0.3 }}
                        />
                        {h.etfDetails && h.etfDetails.length > 0 && (
                          <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.3 }}>
                            {h.etfDetails.map((e, i) => (
                              <span key={i}>
                                {i > 0 && ', '}
                                <b>{e.etfTicker}</b>
                                {e.pctInEtf != null && ` ${fmtPct(e.pctInEtf)}`}
                              </span>
                            ))}
                          </Typography>
                        )}
                      </Box>
                    ) : h.source === 'manual' ? (
                      <Chip label="Manual" size="small" variant="outlined" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Direct</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>{isWeightsOnly ? fmtPct(h.pct) : fmtCurrency(h.value)}</TableCell>
                  <TableCell align="right">{fmtPct(h.pct)}</TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}>No holdings in this class</TableCell></TableRow>
              )}
            </TableBody>
          </MuiTable>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main component ──────────────────────────────────────────────

const PAModal_AssetAllocation = ({ data = [], source = 'portfolio', etfStockPositions = [], etfLeftoverPositions = [], dataProfile, fixedIncomeData, yfData = {}, pdfMode = false, userId, onCreateJiraTicket }) => {
  const [tab, setTab] = useState(0);
  const [showEnrichment, setShowEnrichment] = useState(false);
  const [enrichments, setEnrichments] = useState({});

  const allocation = useMemo(
    () => computeAllocation(data, enrichments, etfStockPositions, etfLeftoverPositions),
    [data, enrichments, etfStockPositions, etfLeftoverPositions]
  );

  const effectiveFixedIncomeData = useMemo(() => {
    if (!allocation || allocation.classificationOnly) return null;

    const byType = {};
    const holdingsByType = {};
    let total = 0;

    const addIncome = (typeKey, income, holding) => {
      byType[typeKey] = (byType[typeKey] || 0) + income;
      if (!holdingsByType[typeKey]) holdingsByType[typeKey] = [];
      holdingsByType[typeKey].push(holding);
      total += income;
    };

    const couponFromName = (name) => {
      const m = (name || '').match(/(\d+\.?\d*)%/);
      return m ? parseFloat(m[1]) : null;
    };

    const findOrigItem = (ticker) =>
      data.find(d => (d.Ticker || d.ticker || '').toUpperCase() === ticker);

    const allClasses = allocation.holdings_by_class || {};
    for (const [className, holdings] of Object.entries(allClasses)) {
      if (className === 'Cash' || className === 'Unclassified') continue;
      for (const h of (holdings || [])) {
        const ticker = (h.ticker || '').toUpperCase();
        const yf = yfData[ticker];
        const origItem = findOrigItem(ticker);

        let yieldPct = null;
        let yieldSource = '';
        if (className === 'Fixed Income') {
          const coupon = parseFloat(origItem?.coupon_percentage);
          const manualPct = parseFloat(origItem?.manual_percentage);
          const divPct = parseFloat(origItem?.dividend_percentage);
          const yfDiv = parseFloat(yf?.Dividend);
          const nameCoupon = couponFromName(h.name);

          if (manualPct > 0) { yieldPct = manualPct; yieldSource = 'manual'; }
          else if (coupon > 0) { yieldPct = coupon; yieldSource = 'coupon'; }
          else if (yfDiv > 0) { yieldPct = yfDiv; yieldSource = 'YF dividend'; }
          else if (divPct > 0) { yieldPct = divPct; yieldSource = 'div yield'; }
          else if (nameCoupon > 0 && nameCoupon <= 20) { yieldPct = nameCoupon; yieldSource = 'from name'; }
        } else {
          const yfDiv = parseFloat(yf?.Dividend);
          const divPct = parseFloat(origItem?.dividend_percentage);
          const manualPct = parseFloat(origItem?.manual_percentage);

          if (manualPct > 0) { yieldPct = manualPct; yieldSource = 'manual'; }
          else if (yfDiv > 0) { yieldPct = yfDiv; yieldSource = 'YF dividend'; }
          else if (divPct > 0) { yieldPct = divPct; yieldSource = 'div yield'; }
        }

        if (!yieldPct || yieldPct <= 0 || !h.value) continue;

        const income = h.value * (yieldPct / 100);
        const typeKey = className === 'Fixed Income' ? (h.type || 'Fixed Income') : className;
        addIncome(typeKey, income, { name: h.name, ticker: h.ticker, value: h.value, income, rate: yieldPct, yieldSource });
      }
    }

    for (const h of (allClasses['Cash'] || [])) {
      const ticker = (h.ticker || '').toUpperCase();
      const origItem = findOrigItem(ticker);
      const bankInterest = parseFloat(origItem?.bank_interest_percentage);
      const manualPct = parseFloat(origItem?.manual_percentage);
      const yf = yfData[ticker];
      const dividendYield = parseFloat(yf?.Dividend);

      let ratePct = null;
      let yieldSource = '';
      if (manualPct > 0) { ratePct = manualPct; yieldSource = 'manual'; }
      else if (bankInterest > 0) { ratePct = bankInterest; yieldSource = 'bank interest'; }
      else if (dividendYield && dividendYield > 0) { ratePct = dividendYield; yieldSource = 'YF dividend'; }

      if (ratePct && h.value > 0) {
        const income = h.value * (ratePct / 100);
        addIncome('Cash Interest', income, { name: h.name, ticker: h.ticker, value: h.value, income, rate: ratePct, yieldSource });
      }
    }

    if (total <= 0) return null;

    const distribution = Object.entries(byType)
      .map(([Assettype, fixed_income]) => ({
        Assettype,
        fixed_income,
        percentage: total > 0 ? (fixed_income / total) * 100 : 0,
        holdings: holdingsByType[Assettype] || [],
      }))
      .sort((a, b) => b.fixed_income - a.fixed_income);

    return { total, distribution, estimated: true };
  }, [allocation, yfData, data]);

  const investableOnlyStrategy = useMemo(() => {
    const hasEnrichments = Object.values(enrichments).some(v => v > 0);
    if (!hasEnrichments) return null;
    const brokerOnly = computeAllocation(data, {}, etfStockPositions, etfLeftoverPositions);
    return brokerOnly?.closest_strategy || null;
  }, [data, enrichments, etfStockPositions, etfLeftoverPositions]);

  const enrichmentLabels = useMemo(() => {
    return Object.entries(enrichments)
      .filter(([, v]) => v > 0)
      .map(([key]) => NON_BROKER_CATEGORIES.find(c => c.key === key)?.label || key);
  }, [enrichments]);

  const handleEnrichmentChange = (key, value) => {
    setEnrichments(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  if (!allocation) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <Typography color="text.secondary">No portfolio data available for allocation analysis.</Typography>
      </Box>
    );
  }

  if (allocation.classificationOnly) {
    const classSummaryAsHoldings = Object.fromEntries(
      allocation.class_summary.map(({ name, holdings }) => [name, holdings])
    );
    return (
      <Box>
        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
          No position values or weights were found — percentage breakdown and strategy comparison are unavailable.
          However, {allocation.total_classified} holding{allocation.total_classified !== 1 ? 's were' : ' was'} classified into asset classes.
        </Alert>
        <HoldingsReport holdingsByClass={classSummaryAsHoldings} onClassClick={() => {}} />
      </Box>
    );
  }

  const { asset_classes = [], closest_strategy, strategy_match, strategy_comparison = {}, strategy_assumptions = {}, total_value = 0, holdings_by_class = {} } = allocation;
  const activeTab = TABS[tab]?.key;

  const isWeightsOnly = dataProfile?.valueSource === 'exposure_pct';

  const jiraBtn = (tabLabel) => userId === 2 && onCreateJiraTicket ? (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
      <Tooltip title={`Create Jira Ticket – ${tabLabel}`}>
        <IconButton size="small" onClick={() => onCreateJiraTicket(`Asset Allocation – ${tabLabel}`)}>
          <ConfirmationNumberIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  ) : null;

  return (
    <Box>
      {!pdfMode && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {TABS.map((t) => <Tab key={t.key} label={t.label} />)}
        </Tabs>
      )}

      {(pdfMode || activeTab === 'overview') && (
        <Box data-pdf-section="Overview" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Overview</Typography>}
          {jiraBtn('Overview')}
          <OverviewTab
            assetClasses={asset_classes}
            closestStrategy={closest_strategy}
            strategyMatch={strategy_match}
            investableOnlyStrategy={investableOnlyStrategy}
            enrichmentLabels={enrichmentLabels}
            totalValue={total_value}
            holdingsByClass={holdings_by_class}
            showEnrichment={showEnrichment}
            setShowEnrichment={setShowEnrichment}
            enrichments={enrichments}
            handleEnrichmentChange={handleEnrichmentChange}
            isWeightsOnly={isWeightsOnly}
            fixedIncomeData={effectiveFixedIncomeData}
          />
        </Box>
      )}
      {(pdfMode || activeTab === 'insights') && (
        <Box data-pdf-section="Insights" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>What This Means</Typography>}
          {jiraBtn('Insights')}
          <InsightsTab
            assetClasses={asset_classes}
            totalValue={total_value}
            holdingsByClass={holdings_by_class}
            isWeightsOnly={isWeightsOnly}
          />
        </Box>
      )}
      {(pdfMode || activeTab === 'strategy') && (
        <Box data-pdf-section="Strategy" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Strategy Comparison</Typography>}
          {jiraBtn('Strategy')}
          <StrategyTab
            strategyComparison={strategy_comparison}
            assetClasses={asset_classes}
            closestStrategy={closest_strategy}
            strategyMatch={strategy_match}
            investableOnlyStrategy={investableOnlyStrategy}
          />
        </Box>
      )}
      {(pdfMode || activeTab === 'assumptions') && (
        <Box data-pdf-section="Assumptions" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Wealth Assumptions</Typography>}
          {jiraBtn('Assumptions')}
          <AssumptionsTab
            strategyAssumptions={strategy_assumptions}
            totalValue={total_value}
            closestStrategy={closest_strategy}
            investableOnlyStrategy={investableOnlyStrategy}
            isWeightsOnly={isWeightsOnly}
          />
        </Box>
      )}
    </Box>
  );
};


// ─── Holdings Report (always shown below pie + breakdown) ───────

const MAX_INLINE_TICKERS = 8;

const HoldingsReport = ({ holdingsByClass = {}, onClassClick }) => {
  const classesWithHoldings = useMemo(
    () => ASSET_CLASS_ORDER
      .filter(name => (holdingsByClass[name] || []).length > 0)
      .map(name => ({ name, holdings: holdingsByClass[name] })),
    [holdingsByClass]
  );
  const totalHoldings = classesWithHoldings.reduce((s, c) => s + c.holdings.length, 0);

  if (totalHoldings === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Holdings per Asset Class ({totalHoldings} total)
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 180 }}>Asset Class</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, width: 60 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Holdings</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {classesWithHoldings.map(({ name, holdings }) => {
              const shown = holdings.slice(0, MAX_INLINE_TICKERS);
              const overflow = holdings.length - MAX_INLINE_TICKERS;
              return (
                <TableRow
                  key={name}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onClassClick(name)}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ASSET_CLASS_COLORS[name] || '#999', flexShrink: 0 }} />
                      {name}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{holdings.length}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {shown.map(h => h.ticker || h.name).join(', ')}
                      {overflow > 0 && ` + ${overflow} more`}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};


// ─── Fixed Income Potential Section (DAS-2161) ──────────────────

const FI_COLORS = ['#7b1fa2', '#ab47bc', '#ce93d8', '#e1bee7', '#f3e5f5', '#9c27b0', '#6a1b9a'];

const YIELD_SOURCE_LABELS = {
  manual: 'Manual',
  coupon: 'Coupon rate',
  'YF dividend': 'Yahoo Finance',
  'div yield': 'Dividend yield',
  'from name': 'Parsed from name',
  'bank interest': 'Bank interest',
};

const FIDetailDialog = ({ open, onClose, category, holdings = [] }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '70vh' } }}>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
      {category}
      <Chip label={`${holdings.length} position${holdings.length !== 1 ? 's' : ''}`} size="small" sx={{ ml: 0.5 }} />
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <TableContainer>
        <MuiTable size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Ticker</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Value (EUR)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Rate</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Annual Income</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {holdings.map((h, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                <TableCell>{h.name || '—'}</TableCell>
                <TableCell>{h.ticker ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{h.ticker}</Typography> : '—'}</TableCell>
                <TableCell align="right">{fmtCurrency(h.value)}</TableCell>
                <TableCell align="right">{h.rate != null ? fmtPct(h.rate) : '—'}</TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    {YIELD_SOURCE_LABELS[h.yieldSource] || h.yieldSource || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>{fmtCurrency(h.income)}</TableCell>
              </TableRow>
            ))}
            {holdings.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}>No constituents available</TableCell></TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </DialogContent>
  </Dialog>
);

const FixedIncomePotentialSection = ({ fixedIncomeData, fiAllocationPct }) => {
  const [fiDetail, setFiDetail] = useState(null);

  if (!fixedIncomeData?.total && fixedIncomeData?.total !== 0) return null;

  const total = fixedIncomeData.total;
  const distribution = fixedIncomeData.distribution || [];
  const hasDistribution = distribution.length > 0;

  const pieData = distribution
    .filter(d => d.fixed_income > 0)
    .sort((a, b) => b.fixed_income - a.fixed_income)
    .map((d, i) => ({
      id: i,
      value: d.fixed_income,
      label: `${d.Assettype} (${fmtCurrency(d.fixed_income)})`,
      color: FI_COLORS[i % FI_COLORS.length],
    }));

  const handleCategoryClick = (d) => {
    if (d.holdings && d.holdings.length > 0) {
      setFiDetail({ category: d.Assettype, holdings: d.holdings });
    }
  };

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderColor: '#7b1fa2', borderWidth: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#7b1fa2', flexShrink: 0 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Fixed Income Potential
          </Typography>
          {fixedIncomeData.estimated && (
            <Chip label="estimated" size="small" variant="outlined" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
          <Tooltip
            arrow
            placement="top"
            title={fixedIncomeData.estimated
              ? "Estimated from dividend yield and deposit interest data. Includes bonds, fixed-income ETFs, and cash/savings interest. Actual income may differ."
              : "Annual income from coupon payments, fixed-income distributions, and cash/savings interest across your holdings."
            }
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={3} alignItems="flex-start">
          <Box flex="0 0 auto">
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
              {fmtCurrency(total)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Annual fixed income potential
            </Typography>
            {fiAllocationPct != null && fiAllocationPct > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
                from {fmtPct(fiAllocationPct)} Fixed Income allocation
              </Typography>
            )}
          </Box>

          {hasDistribution && pieData.length > 0 && (
            <Box flex="1 1 300px" display="flex" flexWrap="wrap" gap={3} alignItems="center">
              <Box flex="0 0 auto">
                <PieChart
                  series={[{
                    data: pieData,
                    innerRadius: 35,
                    outerRadius: 80,
                    paddingAngle: 2,
                    cornerRadius: 3,
                    highlightScope: { fade: 'global', highlight: 'item' },
                  }]}
                  width={200}
                  height={180}
                  slotProps={{ legend: { hidden: true } }}
                />
              </Box>
              <Box flex="1 1 160px">
                {distribution
                  .filter(d => d.fixed_income > 0)
                  .sort((a, b) => b.fixed_income - a.fixed_income)
                  .map((d, i) => (
                    <Box
                      key={d.Assettype}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mb={0.6}
                      sx={{
                        cursor: d.holdings?.length > 0 ? 'pointer' : 'default',
                        borderRadius: 1,
                        px: 0.5,
                        mx: -0.5,
                        '&:hover': d.holdings?.length > 0 ? { bgcolor: 'action.hover' } : undefined,
                      }}
                      onClick={() => handleCategoryClick(d)}
                    >
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: FI_COLORS[i % FI_COLORS.length], flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {d.Assettype}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {fmtCurrency(d.fixed_income)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', minWidth: 42, textAlign: 'right' }}>
                        {fmtPct(d.percentage)}
                      </Typography>
                    </Box>
                  ))}
                {hasDistribution && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                    Click a category to see constituents
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      <FIDetailDialog
        open={!!fiDetail}
        onClose={() => setFiDetail(null)}
        category={fiDetail?.category || ''}
        holdings={fiDetail?.holdings || []}
      />
    </>
  );
};

// ─── Overview Tab ────────────────────────────────────────────────

const OverviewTab = ({
  assetClasses, closestStrategy, strategyMatch, investableOnlyStrategy, enrichmentLabels = [],
  totalValue, holdingsByClass = {},
  showEnrichment, setShowEnrichment, enrichments,
  handleEnrichmentChange, isWeightsOnly = false, fixedIncomeData,
}) => {
  const [detailClass, setDetailClass] = useState(null);

  const pieClassNames = useMemo(() =>
    assetClasses.filter(ac => ac.percentage > 0).map(ac => ac.name),
    [assetClasses]
  );

  const pieData = useMemo(() =>
    assetClasses
      .filter(ac => ac.percentage > 0)
      .map((ac, idx) => ({
        id: idx,
        value: ac.percentage,
        label: `${ac.name} (${fmtPct(ac.percentage)})`,
        color: ASSET_CLASS_COLORS[ac.name] || '#999',
      })),
    [assetClasses]
  );

  const missingClasses = assetClasses.filter(ac => ac.percentage === 0);

  const handlePieClick = useCallback((_event, itemIdentifier) => {
    const className = pieClassNames[itemIdentifier?.dataIndex];
    if (className) setDetailClass(className);
  }, [pieClassNames]);

  const pieTooltipFormatter = useCallback((item, context) => {
    const className = pieClassNames[context?.dataIndex];
    if (!className) return `${item.value}%`;
    const holdings = holdingsByClass[className] || [];
    const top = holdings.slice(0, 4);
    const lines = top.map(h => `  ${h.ticker || h.name}: ${fmtPct(h.pct)}`);
    if (holdings.length > 4) lines.push(`  + ${holdings.length - 4} more`);
    return [fmtPct(item.value), ...lines].join('\n');
  }, [pieClassNames, holdingsByClass]);

  const match = strategyMatch || {};
  const dominantClass = assetClasses.reduce((a, b) => (a.percentage > b.percentage ? a : b), { percentage: 0 });
  const dominantPct = dominantClass.percentage || 0;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This shows how your money is spread across different types of investments.
        Click any category to see which holdings belong to it.
      </Typography>

      {closestStrategy && (
        match.isGoodFit ? (
          <Alert severity="success" icon={false} sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 600 }}>
              Good match: your portfolio fits a "{closestStrategy}" strategy ({match.inRange}/{match.total} categories in range)
            </AlertTitle>
            <Typography variant="body2">
              {STRATEGIES[closestStrategy]?.explanation || ''}
            </Typography>
            {investableOnlyStrategy && investableOnlyStrategy !== closestStrategy && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                📌 Without non-broker assets ({enrichmentLabels.join(', ')}): closest to <strong>{investableOnlyStrategy}</strong>.
              </Typography>
            )}
          </Alert>
        ) : match.isReasonableFit ? (
          <Alert severity="info" icon={false} sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 600 }}>
              Partial match: closest to "{closestStrategy}" but only {match.inRange}/{match.total} categories are in range
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {STRATEGIES[closestStrategy]?.explanation || ''}
            </Typography>
            <Typography variant="body2">
              <strong>What's off:</strong>{' '}
              {(match.misses || []).map(m =>
                `${m.ac} is ${fmtPct(m.actual)} (target: ${m.lo}–${m.hi}%)`
              ).join(', ')}.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 600 }}>
              Your portfolio doesn't match any standard strategy ({match.inRange}/{match.total} categories in range)
            </AlertTitle>
            {dominantPct > 40 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{dominantClass.name}</strong> dominates at {fmtPct(dominantPct)} of your total.
                {dominantClass.name === 'Real Assets' && ' This is likely your property — most investment strategies assume 0–5% in Real Assets because they focus on liquid, tradeable investments. Your home is valuable but it\'s not something you can rebalance.'}
                {dominantClass.name === 'Cash' && ' A large cash position reduces your long-term returns significantly. Consider whether this is intentional (you need the money soon) or if part should be invested.'}
                {dominantClass.name === 'Equity' && ' This is an extremely stock-heavy allocation with little diversification into bonds or other asset classes.'}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 1 }}>
              The nearest strategy is <strong>{closestStrategy}</strong> ({STRATEGIES[closestStrategy]?.explanation || ''}), but your numbers are far outside its ranges:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {(match.misses || []).slice(0, 4).map((m, i) => (
                <Typography component="li" variant="body2" key={i}>
                  <strong>{m.ac}:</strong> you have {fmtPct(m.actual)}, target is {m.lo}–{m.hi}% (off by {fmtPct(m.diff)})
                </Typography>
              ))}
            </Box>
            {investableOnlyStrategy && investableOnlyStrategy !== closestStrategy && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                📌 Without non-broker assets: closest to <strong>{investableOnlyStrategy}</strong>.
              </Typography>
            )}
          </Alert>
        )
      )}

      <Box display="flex" flexWrap="wrap" gap={3} mb={3}>
        <Box flex="1 1 340px" minWidth={300}>
          {pieData.length > 0 ? (
            <PieChart
              series={[{
                data: pieData,
                innerRadius: 50,
                outerRadius: 120,
                paddingAngle: 2,
                cornerRadius: 4,
                highlightScope: { fade: 'global', highlight: 'item' },
                valueFormatter: pieTooltipFormatter,
              }]}
              width={380}
              height={280}
              slotProps={{ legend: { hidden: true } }}
              onItemClick={handlePieClick}
            />
          ) : (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No allocation data
            </Typography>
          )}
        </Box>

        <Box flex="1 1 300px">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            Asset Class Breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {isWeightsOnly
              ? 'Source: exposure weights (no absolute values in file)'
              : `Total portfolio value: ${fmtCurrency(totalValue)}`}
          </Typography>

          {ASSET_CLASS_ORDER.map((name) => {
            const ac = assetClasses.find(a => a.name === name) || { percentage: 0, value_eur: 0 };
            const color = ASSET_CLASS_COLORS[name];
            const isPresent = ac.percentage > 0;
            const holdings = holdingsByClass[name] || [];

            return (
              <Tooltip
                key={name}
                title={isPresent ? <HoldingsTooltipContent holdings={holdings} className={name} /> : ''}
                placement="left"
                arrow
                enterDelay={200}
                slotProps={{ tooltip: { sx: { bgcolor: 'rgba(33,33,33,0.95)', p: 1.2, maxWidth: 300 } } }}
              >
                <Box
                  sx={{
                    mb: 1,
                    opacity: isPresent ? 1 : 0.4,
                    cursor: isPresent ? 'pointer' : 'default',
                    borderRadius: 1,
                    px: 0.5,
                    '&:hover': isPresent ? { bgcolor: 'action.hover' } : undefined,
                  }}
                  onClick={() => isPresent && setDetailClass(name)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.3}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontWeight: isPresent ? 500 : 400 }}>
                        {name}
                      </Typography>
                      <AssetClassInfoIcon className={name} />
                      {isPresent && (
                        <Typography variant="caption" color="text.secondary">
                          ({holdings.length})
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {isPresent
                        ? isWeightsOnly
                          ? fmtPct(ac.percentage)
                          : `${fmtPct(ac.percentage)}  ·  ${fmtCurrency(ac.value_eur)}`
                        : 'Not provided'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(ac.percentage, 100)}
                    sx={{
                      height: 6, borderRadius: 3,
                      bgcolor: '#f0f0f0',
                      '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
                    }}
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      <HoldingsReport holdingsByClass={holdingsByClass} onClassClick={setDetailClass} />

      <FixedIncomePotentialSection
        fixedIncomeData={fixedIncomeData}
        fiAllocationPct={assetClasses.find(ac => ac.name === 'Fixed Income')?.percentage}
      />

      {missingClasses.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Your broker portfolio doesn't cover everything</AlertTitle>
          Your broker account has no {missingClasses.map(ac => ac.name).join(', ')}.
          But you might own these outside your broker (e.g. your home = Real Assets, your pension = Fixed Income).
          Use the button below to add them — it changes the strategy match and gives a more complete picture.
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setShowEnrichment(!showEnrichment)}
          sx={{ textTransform: 'none' }}
        >
          {showEnrichment ? 'Hide additional assets' : 'Add assets held outside this broker?'}
        </Button>
      </Box>

      <Collapse in={showEnrichment}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Add the estimated value of assets held outside this broker:
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            This gives a more complete picture of your total wealth and changes which investment strategy your portfolio resembles.
            Don't worry about exact figures — rough estimates work fine.
          </Typography>
          {NON_BROKER_CATEGORIES.map(({ key, label, icon }) => (
            <Box key={key} display="flex" alignItems="center" gap={1} mb={1}>
              <Typography sx={{ width: 24, textAlign: 'center' }}>{icon}</Typography>
              <Typography variant="body2" sx={{ flex: '1 1 200px', minWidth: 180 }}>{label}</Typography>
              <TextField
                size="small"
                type="number"
                placeholder="€ value"
                value={enrichments[key] || ''}
                onChange={(e) => handleEnrichmentChange(key, e.target.value)}
                sx={{ width: 140 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
              />
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Values recalculate automatically as you type.
          </Typography>
        </Paper>
      </Collapse>

      <AssetClassDetailDialog
        open={!!detailClass}
        onClose={() => setDetailClass(null)}
        className={detailClass || ''}
        holdings={detailClass ? (holdingsByClass[detailClass] || []) : []}
        totalValue={totalValue}
        isWeightsOnly={isWeightsOnly}
      />
    </Box>
  );
};


// ─── Strategy Comparison Tab ─────────────────────────────────────

const StrategyTab = ({ strategyComparison, assetClasses, closestStrategy, strategyMatch, investableOnlyStrategy }) => {
  const primaryStrategy = investableOnlyStrategy && investableOnlyStrategy !== closestStrategy
    ? investableOnlyStrategy : closestStrategy;
  const matchDetails = strategyMatch?.allDetails || {};
  const pctMap = useMemo(
    () => Object.fromEntries(assetClasses.map(ac => [ac.name, ac.percentage])),
    [assetClasses]
  );

  const unclassifiedPct = pctMap['Unclassified'] || 0;
  const strategyNames = Object.keys(strategyComparison);

  const barData = useMemo(() =>
    INVESTABLE_CLASSES.map(ac => {
      const row = { asset_class: ac, 'Your Portfolio': pctMap[ac] || 0 };
      for (const sName of strategyNames) {
        const ranges = strategyComparison[sName]?.asset_classes?.[ac];
        if (ranges) row[sName] = (ranges.range_low + ranges.range_high) / 2;
      }
      return row;
    }),
    [pctMap, strategyComparison, strategyNames]
  );

  const seriesConfig = useMemo(() => {
    const colors = ['#1976d2', '#e65100', '#7b1fa2', '#2e7d32', '#c62828', '#455a64', '#f57c00'];
    return ['Your Portfolio', ...strategyNames].map((name, idx) => ({
      dataKey: name,
      label: name,
      color: colors[idx % colors.length],
    }));
  }, [strategyNames]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This table compares your portfolio's asset allocation to 6 well-known investment strategies.
        Each strategy has a recommended range (e.g. "50–60%") for every asset class.{' '}
        <strong style={{ color: '#2e7d32' }}>Green numbers</strong> mean your portfolio falls within that strategy's recommended range for that asset class.
      </Typography>

      {primaryStrategy && (
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          Your portfolio most closely matches <strong>{primaryStrategy}</strong>: {STRATEGIES[primaryStrategy]?.explanation || ''}
          {investableOnlyStrategy && investableOnlyStrategy !== closestStrategy && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              📌 With non-broker assets included: <strong>{closestStrategy}</strong>
            </Typography>
          )}
        </Alert>
      )}

      {unclassifiedPct > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{fmtPct(unclassifiedPct)}</strong> of your portfolio could not be classified and is excluded from this comparison.
          Review the Unclassified bucket in the Overview tab.
        </Alert>
      )}

      <Box sx={{ mb: 3, overflowX: 'auto' }}>
        <BarChart
          dataset={barData}
          xAxis={[{ scaleType: 'band', dataKey: 'asset_class' }]}
          yAxis={[{ label: '% of portfolio' }]}
          series={seriesConfig}
          height={340}
          margin={{ left: 50, right: 20, top: 20, bottom: 40 }}
          slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' } } }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '50vh' }}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Asset Class</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#e3f2fd' }}>Your Portfolio</TableCell>
              {strategyNames.map(name => (
                <TableCell key={name} align="center" sx={{
                  fontWeight: 600,
                  bgcolor: name === primaryStrategy ? '#e8f5e9' : undefined,
                }}>
                  {name}
                  {name === primaryStrategy && (
                    <Chip label="closest" size="small" color="success" sx={{ ml: 0.5, height: 18, fontSize: 10 }} />
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {INVESTABLE_CLASSES.map(ac => (
              <TableRow key={ac} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ASSET_CLASS_COLORS[ac] }} />
                    {ac}
                    <AssetClassInfoIcon className={ac} />
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, bgcolor: '#e3f2fd' }}>
                  {fmtPct(pctMap[ac] || 0)}
                </TableCell>
                {strategyNames.map(name => {
                  const ranges = strategyComparison[name]?.asset_classes?.[ac];
                  if (!ranges) return <TableCell key={name} align="center">—</TableCell>;
                  const actual = pctMap[ac] || 0;
                  const inRange = actual >= ranges.range_low && actual <= ranges.range_high;
                  const diff = !inRange ? (actual < ranges.range_low ? ranges.range_low - actual : actual - ranges.range_high) : 0;
                  const isFarOff = diff > 15;
                  return (
                    <TableCell key={name} align="center" sx={{
                      bgcolor: name === primaryStrategy ? '#e8f5e9' : undefined,
                      color: inRange ? '#2e7d32' : isFarOff ? '#c62828' : undefined,
                      fontWeight: inRange ? 600 : 400,
                    }}>
                      {ranges.range_low}–{ranges.range_high}%
                      {isFarOff && (
                        <Typography variant="caption" display="block" sx={{ color: '#c62828', fontSize: '0.65rem', lineHeight: 1.2 }}>
                          off by {fmtPct(Math.round(diff * 10) / 10)}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}>
              <TableCell sx={{ fontWeight: 700 }}>Your fit</TableCell>
              <TableCell align="center" sx={{ bgcolor: '#e3f2fd' }}>—</TableCell>
              {strategyNames.map(name => {
                const d = matchDetails[name];
                const count = d?.inRange || 0;
                const total = d?.total || 6;
                const color = count >= 4 ? '#2e7d32' : count >= 2 ? '#e65100' : '#c62828';
                const label = count >= 4 ? 'Good fit' : count >= 2 ? 'Partial' : 'Poor fit';
                return (
                  <TableCell key={name} align="center" sx={{
                    fontWeight: 700,
                    color,
                    bgcolor: name === primaryStrategy ? '#e8f5e9' : undefined,
                  }}>
                    {count}/{total} in range
                    <Typography variant="caption" display="block" sx={{ color, fontWeight: 600, fontSize: '0.7rem' }}>
                      {label}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Profile</TableCell>
              <TableCell align="center" sx={{ bgcolor: '#e3f2fd' }}>—</TableCell>
              {strategyNames.map(name => (
                <TableCell key={name} align="center" sx={{
                  fontStyle: 'italic', fontSize: '0.75rem', color: 'text.secondary',
                  bgcolor: name === primaryStrategy ? '#e8f5e9' : undefined,
                }}>
                  {strategyComparison[name]?.profile}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};


// ─── Wealth Assumptions Tab ──────────────────────────────────────

const AssumptionsTab = ({ strategyAssumptions, totalValue, closestStrategy, investableOnlyStrategy, isWeightsOnly = false }) => {
  const primaryStrategy = investableOnlyStrategy && investableOnlyStrategy !== closestStrategy
    ? investableOnlyStrategy : closestStrategy;
  const entries = Object.entries(strategyAssumptions);

  if (isWeightsOnly) {
    return (
      <Alert severity="warning" icon={<InfoOutlinedIcon />} sx={{ my: 4 }}>
        Wealth assumptions require absolute portfolio values (EUR). This file only contains exposure weights — no monetary values are available to compute implied total wealth.
      </Alert>
    );
  }

  if (entries.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        Insufficient data to compute wealth assumptions.
      </Typography>
    );
  }

  const eqPct = entries.length > 0 ? (() => {
    const first = entries[0][1];
    return first?.broker_share_pct ? Math.round(totalValue / (first.implied_total_wealth || 1) * 100 * 10) / 10 : null;
  })() : null;

  return (
    <Box>
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
        <AlertTitle sx={{ fontWeight: 600 }}>What is this page?</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Your broker account holds {fmtCurrency(totalValue)}. But that's probably not everything you own — you also have a home, savings, pension, etc.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          This table answers: <strong>"If I wanted to follow strategy X, what percentage of my total wealth should this broker account be?"</strong>
        </Typography>
        <Typography variant="body2">
          For example: a "Growth" strategy says ~75% should be in stocks. Your stocks are {eqPct != null ? fmtPct(eqPct) : '—'} of your broker portfolio.
          If that broker portfolio is your ONLY investment, then {eqPct != null ? fmtPct(eqPct) : '—'} is also your overall stock allocation.
          But if you also own a house worth €300k, your overall stock percentage is much lower — because the house (Real Assets) dilutes it.
        </Typography>
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <MuiTable size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Strategy</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                <Tooltip title="The equity target for this strategy (e.g. Growth = 70–80%)" arrow>
                  <span>Equity Target</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                <Tooltip title="How much total wealth you would need so that your stocks are exactly at this strategy's equity target" arrow>
                  <span>Your Total Wealth Should Be</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                <Tooltip title="How much of your total wealth your broker account would represent" arrow>
                  <span>Broker = % of Total</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Who is this for?</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([name, row]) => {
              const eqRange = STRATEGIES[name]?.Equity || [0, 0];
              const isTooSmall = row.broker_share_pct > 100;
              return (
              <TableRow key={name} hover sx={{
                bgcolor: name === primaryStrategy ? '#e8f5e9' : undefined,
              }}>
                <TableCell sx={{ fontWeight: name === primaryStrategy ? 700 : 400 }}>
                  {name}
                  {name === primaryStrategy && (
                    <Chip label="closest" size="small" color="success" sx={{ ml: 0.5, height: 18, fontSize: 10 }} />
                  )}
                </TableCell>
                <TableCell align="right">
                  {eqRange[0]}–{eqRange[1]}%
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {isTooSmall ? (
                    <Tooltip title={`Your broker already has more equity than this strategy needs — you don't need outside assets for ${name}`} arrow>
                      <span style={{ color: '#9e9e9e' }}>n/a — broker alone exceeds target</span>
                    </Tooltip>
                  ) : fmtCurrency(row.implied_total_wealth)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {isTooSmall ? '> 100%' : fmtPct(row.broker_share_pct)}
                </TableCell>
                <TableCell sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem' }}>
                  {STRATEGIES[name]?.explanation ? STRATEGIES[name].explanation.split('.')[0] + '.' : row.profile}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};


// ─── InsightsTab ─────────────────────────────────────────────────

const INSIGHT_RULES = [
  {
    id: 'property-dominant',
    test: (pct) => (pct['Real Assets'] || 0) > 40,
    render: (pct, totalValue) => {
      const ra = pct['Real Assets'] || 0;
      const euroValue = totalValue * ra / 100;
      const drop20 = euroValue * 0.2;
      return {
        severity: 'warning',
        text: `${ra.toFixed(0)}% of your wealth is in Real Assets (property). `
            + `That's about €${Math.round(euroValue).toLocaleString('nl-NL')}. `
            + `If the housing market drops 20%, your net worth drops roughly €${Math.round(drop20).toLocaleString('nl-NL')}. `
            + `Property is also illiquid — you can't quickly sell part of it if you need cash.`,
      };
    },
  },
  {
    id: 'high-equity',
    test: (pct) => (pct['Equity'] || 0) > 80,
    render: (pct, totalValue) => {
      const eq = pct['Equity'] || 0;
      const crash = totalValue * eq / 100 * 0.4;
      return {
        severity: 'warning',
        text: `${eq.toFixed(0)}% in Equity is very aggressive. `
            + `A 2008-style crash (−40%) would wipe roughly €${Math.round(crash).toLocaleString('nl-NL')} from your portfolio. `
            + `That's fine if you won't need this money for 10+ years, but risky if you might.`,
      };
    },
  },
  {
    id: 'high-cash',
    test: (pct) => (pct['Cash'] || 0) > 15,
    render: (pct, totalValue) => {
      const c = pct['Cash'] || 0;
      const cashEur = totalValue * c / 100;
      const inflationLoss = cashEur * 0.03;
      return {
        severity: 'info',
        text: `${c.toFixed(0)}% in Cash (€${Math.round(cashEur).toLocaleString('nl-NL')}). `
            + `At ~3% inflation, that's roughly €${Math.round(inflationLoss).toLocaleString('nl-NL')}/year in lost purchasing power. `
            + `Some cash is essential for emergencies, but anything beyond 3-6 months of expenses is slowly losing value.`,
      };
    },
  },
  {
    id: 'low-equity',
    test: (pct) => (pct['Equity'] || 0) < 25 && (pct['Equity'] || 0) > 0,
    render: (pct) => {
      const eq = pct['Equity'] || 0;
      return {
        severity: 'info',
        text: `Only ${eq.toFixed(0)}% in Equity. Historically, equities outperform other asset classes over long periods (7-10% annually). `
            + `A low equity allocation limits long-term growth potential — fine if you're near retirement, less ideal with a long horizon.`,
      };
    },
  },
  {
    id: 'low-fixed-income',
    test: (pct) => (pct['Fixed Income'] || 0) < 5 && (pct['Equity'] || 0) > 20,
    render: () => ({
      severity: 'info',
      text: `Very little Fixed Income. Bonds and deposits act as a cushion during stock market crashes — they tend to hold value or rise when equities fall. `
          + `Without them, your portfolio has no built-in shock absorber.`,
    }),
  },
  {
    id: 'crypto-exposure',
    test: (pct) => (pct['Crypto'] || 0) > 5,
    render: (pct) => {
      const cr = pct['Crypto'] || 0;
      return {
        severity: 'warning',
        text: `${cr.toFixed(0)}% in Crypto. Crypto can drop 50-80% in a single bear market. `
            + `Most financial planners cap crypto at 5% of total wealth. Your exposure is above that threshold.`,
      };
    },
  },
  {
    id: 'good-diversification',
    test: (pct) => {
      const classes = ['Equity', 'Fixed Income', 'Cash', 'Real Assets', 'Alternatives', 'Crypto'];
      return classes.filter(c => (pct[c] || 0) > 10).length >= 3;
    },
    render: (pct) => {
      const classes = ['Equity', 'Fixed Income', 'Cash', 'Real Assets', 'Alternatives', 'Crypto'];
      const spread = classes.filter(c => (pct[c] || 0) > 10);
      return {
        severity: 'success',
        text: `Your wealth is spread across ${spread.length} meaningful asset classes (${spread.join(', ')}). `
            + `This diversification means no single market event can dominate your outcome — that's genuinely good positioning.`,
      };
    },
  },
];

function buildSuggestions(pctMap) {
  const suggestions = [];
  const cash = pctMap['Cash'] || 0;
  const equity = pctMap['Equity'] || 0;
  const fi = pctMap['Fixed Income'] || 0;
  const ra = pctMap['Real Assets'] || 0;

  if (cash > 20) {
    suggestions.push(
      `You have ${cash.toFixed(0)}% in cash. Consider whether some of that could earn more in a deposit account (fixed income) or a broad equity ETF. Even moving a fraction helps offset inflation.`
    );
  }
  if (equity > 80 && fi < 10) {
    suggestions.push(
      `With ${equity.toFixed(0)}% in equities and only ${fi.toFixed(0)}% in bonds, adding some fixed income (government bonds, bond ETFs) would reduce crash severity without drastically cutting returns.`
    );
  }
  if (ra > 60) {
    suggestions.push(
      `Property makes up ${ra.toFixed(0)}% of your wealth. That's largely illiquid and concentrated in one market. You can't easily rebalance real estate, but being aware of the concentration helps with other decisions — e.g., keeping more liquid reserves.`
    );
  }
  if (equity < 15 && cash > 10) {
    suggestions.push(
      `Low equity (${equity.toFixed(0)}%) combined with high cash (${cash.toFixed(0)}%) is a defensive stance. If your time horizon is 5+ years, gradually shifting some cash into equities (e.g., via monthly ETF purchases) could improve long-term returns.`
    );
  }
  return suggestions;
}

const InsightsTab = ({ assetClasses, totalValue, holdingsByClass, isWeightsOnly }) => {
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const pctMap = useMemo(() => {
    if (!assetClasses || !assetClasses.length) return {};
    const m = {};
    for (const a of assetClasses) {
      m[a.name] = a.percentage || 0;
    }
    return m;
  }, [assetClasses]);

  const dominantClass = useMemo(() => {
    let best = null, bestPct = 0;
    for (const [cls, pct] of Object.entries(pctMap)) {
      if (cls === 'Unclassified') continue;
      if (pct > bestPct) { bestPct = pct; best = cls; }
    }
    return best ? { name: best, pct: bestPct } : null;
  }, [pctMap]);

  const observations = useMemo(() => {
    if (!Object.keys(pctMap).length) return [];
    return INSIGHT_RULES
      .filter(r => r.test(pctMap))
      .map(r => ({ id: r.id, ...r.render(pctMap, totalValue) }));
  }, [pctMap, totalValue]);

  const suggestions = useMemo(() => buildSuggestions(pctMap), [pctMap]);

  if (!assetClasses || !assetClasses.length) {
    return <Typography color="text.secondary">No allocation data available.</Typography>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Plain-language observations about your portfolio. No jargon, no formulas —
        just what your numbers actually mean in practice.
      </Typography>

      {/* Section 1: Concentration */}
      {dominantClass && dominantClass.pct > 30 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderLeft: '4px solid #e65100' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Concentration
          </Typography>
          <Typography variant="body2">
            Your largest asset class is <strong>{dominantClass.name}</strong> at{' '}
            <strong>{dominantClass.pct.toFixed(0)}%</strong> of your total wealth.
            {dominantClass.pct > 50
              ? " More than half your net worth depends on a single asset type \u2014 that's high concentration."
              : dominantClass.pct > 40
              ? " That's a significant tilt toward one asset type."
              : " That's a noticeable tilt, though not extreme."}
          </Typography>
        </Paper>
      )}

      {/* Section 2: Key Observations */}
      {observations.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Key Observations
          </Typography>
          {observations.map((obs) => (
            <Alert key={obs.id} severity={obs.severity} sx={{ mb: 1 }} icon={false}>
              <Typography variant="body2">{obs.text}</Typography>
            </Alert>
          ))}
        </Box>
      )}

      {observations.length === 0 && (
        <Alert severity="success" sx={{ mb: 2 }} icon={false}>
          <Typography variant="body2">
            No significant risk flags detected. Your allocation looks balanced across asset classes.
          </Typography>
        </Alert>
      )}

      {/* Section 3: Suggestions (collapsed) */}
      {suggestions.length > 0 && (
        <Box>
          <Button
            size="small"
            onClick={() => setSuggestionsOpen(!suggestionsOpen)}
            endIcon={<ExpandMoreIcon sx={{
              transform: suggestionsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }} />}
            sx={{ textTransform: 'none', mb: 1 }}
          >
            {suggestionsOpen ? 'Hide suggestions' : 'If you wanted to change something…'}
          </Button>
          <Collapse in={suggestionsOpen}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                These are not recommendations — just mechanical observations about your numbers.
                This is not financial advice.
              </Typography>
              {suggestions.map((s, i) => (
                <Typography key={i} variant="body2" sx={{ mb: i < suggestions.length - 1 ? 1 : 0 }}>
                  • {s}
                </Typography>
              ))}
            </Paper>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default PAModal_AssetAllocation;
